import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  criarColaborador,
  desativarEmpresa,
  desvincularColaborador,
  editarColaborador,
  editarEmpresa,
  enviarSpeds,
  salvarDadosBancarios,
  simularPassivo,
  type ColaboradorCriado,
  type DadosBancarios,
  type DadosDeColaborador,
  type DadosDeEmpresa,
  type EntradaSimulacaoPassivo,
  type ResultadoSimulacaoPassivo,
  type ResultadoUploadSped,
} from '../servicos/escrita';
import type { EmpresaResumo } from '../servicos/empresas';

/**
 * Mutações.
 *
 * ## A invalidação é o ponto
 *
 * Cada mutação declara o que ficou velho. Sem isso, cadastrar um colaborador e
 * voltar para a lista mostraria a lista antiga — e o usuário clicaria em
 * "cadastrar" de novo, criando o problema que a tela deveria ter evitado.
 *
 * `invalidateQueries` com prefixo derruba a árvore inteira daquele assunto:
 * `['empresas']` cobre todas as páginas e todos os termos de busca, porque
 * qualquer uma delas pode conter a empresa que acabou de mudar.
 */

export function useEditarEmpresa(
  businessId: string,
): UseMutationResult<EmpresaResumo, Error, Partial<Omit<DadosDeEmpresa, 'taxpayerId'>>> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: Partial<Omit<DadosDeEmpresa, 'taxpayerId'>>) =>
      editarEmpresa(businessId, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['empresas'] });
      void cliente.invalidateQueries({ queryKey: ['empresa', businessId] });
    },
  });
}

export function useDesativarEmpresa(): UseMutationResult<void, Error, string> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: desativarEmpresa,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['empresas'] });
      // Desativar a empresa derruba os vínculos junto, então a carteira também
      // muda: uma empresa desativada deixa de ser cliente ou fornecedora.
      void cliente.invalidateQueries({ queryKey: ['carteira'] });
    },
  });
}

export function useCriarColaborador(
  businessId: string,
): UseMutationResult<ColaboradorCriado, Error, DadosDeColaborador> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: DadosDeColaborador) => criarColaborador(businessId, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['colaboradores', businessId] });
    },
  });
}

export function useEditarColaborador(
  businessId: string,
): UseMutationResult<
  ColaboradorCriado,
  Error,
  { accountId: string; dados: Parameters<typeof editarColaborador>[2] }
> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      dados,
    }: {
      accountId: string;
      dados: Parameters<typeof editarColaborador>[2];
    }) => editarColaborador(businessId, accountId, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['colaboradores', businessId] });
    },
  });
}

export function useDesvincularColaborador(
  businessId: string,
): UseMutationResult<void, Error, string> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => desvincularColaborador(businessId, accountId),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['colaboradores', businessId] });
    },
  });
}

export function useSalvarDadosBancarios(
  businessId: string,
): UseMutationResult<DadosBancarios, Error, DadosBancarios> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: DadosBancarios) => salvarDadosBancarios(businessId, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['dados-bancarios', businessId] });
    },
  });
}

export function useEnviarSpeds(
  clienteId: string,
): UseMutationResult<ResultadoUploadSped, Error, readonly File[]> {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (arquivos: readonly File[]) => enviarSpeds(clienteId, arquivos),
    onSuccess: () => {
      // O upload cria cálculos e pode criar o cliente: cálculo, dashboard e
      // carteira mudam de uma vez.
      void cliente.invalidateQueries({ queryKey: ['speds', clienteId] });
      void cliente.invalidateQueries({ queryKey: ['dashboard-cliente', clienteId] });
      void cliente.invalidateQueries({ queryKey: ['carteira'] });
    },
  });
}

export function useSimularPassivo(): UseMutationResult<
  ResultadoSimulacaoPassivo,
  Error,
  EntradaSimulacaoPassivo
> {
  // Simulação não persiste nada, então não invalida nada. É mutação por ser
  // operação nova a cada envio, não por alterar estado no servidor.
  return useMutation({ mutationFn: simularPassivo, retry: false });
}
